import React, { useEffect, FC } from "react";
import {
  Box,
  Button,
  IconButton,
  Stack,
  SvgIcon,
  TextField,
  Typography,
} from "@mui/material";
import { useForm, useFieldArray, Controller, Control } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { tokens } from "src/locales/tokens";
import { useTranslation } from "react-i18next";
import LoadingButton from "@mui/lab/LoadingButton/LoadingButton";
import PlusIcon from "@untitled-ui/icons-react/build/esm/Plus";
import DeleteIcon from "@mui/icons-material/Delete";

// Define the field item type
interface FieldItem {
  value: string;
}

// Define the Zod schema
const createValidationSchema = (t: any) =>
  z.object({
    items: z
      .array(
        z.object({
          value: z
            .string()
            .max(255, t(tokens.general.validators.maxLength255) as string)
            .min(1, t(tokens.general.validators.required) as string),
        })
      )
      .min(1, t(tokens.general.validators.required) as string),
  });

// Define the Values type using Zod inference
type Values = z.infer<ReturnType<typeof createValidationSchema>>;

interface ListFormProps {
  values: string[];
  maxItems?: number;
  placeholder?: string;
  isSubmitting?: boolean;
  onSubmit: (items: string[]) => void;
  onChange: (items: string[]) => void;
}

export const ListForm: FC<ListFormProps> = (props) => {
  const { onSubmit, onChange, values, placeholder, isSubmitting, maxItems } =
    props;
  const { t } = useTranslation();

  // Create the validation schema using the function
  const validationSchema = createValidationSchema(t);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<Values>({
    defaultValues: {
      items: values.map((value) => ({ value })),
    },
    resolver: zodResolver(validationSchema),
  });

  const { fields, append, remove } = useFieldArray({
    control: control as Control<Values>,
    name: "items",
  });

  useEffect(() => {
    reset({ items: values.map((value) => ({ value })) });
  }, [values, reset]);

  return (
    <form
      onSubmit={handleSubmit((data) =>
        onSubmit(data.items.map((item) => item.value))
      )}
    >
      <Stack spacing={3}>
        {fields.map((field, index) => (
          <Stack
            key={field.id}
            direction={"row"}
            alignItems={"center"}
            spacing={1}
          >
            <Controller
              name={`items.${index}.value`}
              control={control as Control<Values>}
              render={({ field }) => (
                <TextField
                  {...field}
                  error={Boolean(errors.items?.[index]?.value)}
                  helperText={errors.items?.[index]?.value?.message}
                  placeholder={placeholder ?? ""}
                  fullWidth
                  multiline
                  sx={{
                    "& input": { p: 2 },
                    "& textarea": { p: 2 },
                  }}
                  InputProps={{ sx: { p: 0 } }}
                  onBlur={(e) => {
                    field.onBlur();
                    const updatedValues = fields.map((f, i) =>
                      i === index
                        ? e.target.value
                        : (f as unknown as FieldItem).value
                    );
                    onChange(updatedValues);
                  }}
                />
              )}
            />
            <Box sx={{ pt: 0 }}>
              <IconButton
                aria-label="delete"
                onClick={() => {
                  remove(index);
                  onChange(
                    fields
                      .filter((_, i) => i !== index)
                      .map((f) => (f as unknown as FieldItem).value)
                  );
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          </Stack>
        ))}
        {fields.length === 0 && (
          <Typography
            color={"error"}
            variant="body2"
          >
            {t(tokens.general.validators.oneItemRequired)}
          </Typography>
        )}
        {(!maxItems || fields.length < maxItems) && (
          <Box>
            <Button
              onClick={() => {
                append({ value: "" });
                onChange([
                  ...fields.map((f) => (f as unknown as FieldItem).value),
                  "",
                ]);
              }}
              size="small"
              startIcon={
                <SvgIcon>
                  <PlusIcon />
                </SvgIcon>
              }
              variant="text"
            >
              {t(tokens.general.buttons.addItem)}
            </Button>
          </Box>
        )}
        <Box>
          <LoadingButton
            type="submit"
            size="large"
            loading={isSubmitting}
            variant="contained"
            sx={{
              minWidth: 200,
              height: 56,
            }}
          >
            {t(tokens.general.buttons.next)}
          </LoadingButton>
        </Box>
      </Stack>
    </form>
  );
};
